import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../services/apiClient';
import { clientService } from '../../services/clientService';
import { useI18n } from '../../contexts/I18nContext';
import './TemplateImpactPreviewModal.css';

/** Payload sent to /v2/config-templates/preview-impact. */
export interface TemplateImpactPreviewRequest {
  template_id?: number | null;
  name?: string | null;
  device_type: string;
  json_config: Record<string, unknown>;
  /** Scope of a new template: [owner client id], or null / [] for an ANEP template. */
  client_ids?: string[] | null;
}

interface ClientImpact {
  client_id: string | null;
  total_in_scope: number;
  /** null when no devices for this client were in the assembly sample —
   *  UI shows "—" rather than an untrustworthy 0. */
  total_affected: number | null;
  sampled: number;
}

interface ImpactResponse {
  total_in_scope: number;
  total_affected: number;
  total_in_scope_sampled?: number;
  truncated: boolean;
  /** Per-device diffs from the server — intentionally not rendered in the
   *  modal (doesn't scale to high-volume scopes). Kept on the type so the
   *  caller can still pipe them elsewhere if needed. */
  diffs?: unknown[];
  by_client?: ClientImpact[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called when the user confirms after reviewing the preview. The
   * caller is responsible for running the actual create/edit API
   * call — this modal only displays the dry-run.
   */
  onConfirm: () => void | Promise<void>;
  /** The proposed template body. Built by the caller. */
  request: TemplateImpactPreviewRequest | null;
  /** If true, disable the confirm button — e.g. while caller's save is in flight. */
  confirming?: boolean;
}

/**
 * Aggregate impact summary for a proposed template create/edit.
 *
 * Shows totals + a per-client breakdown. Deliberately does NOT render
 * per-device diffs: at realistic fleet sizes (tens of thousands of
 * devices) that table was more noise than signal, and the aggregate
 * view already tells the reviewer what they need to decide.
 *
 * Dry-run only — the confirm button hands control back to the caller.
 */
export const TemplateImpactPreviewModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  request,
  confirming = false,
}) => {
  const { t } = useI18n();
  const axios = useMemo(() => apiClient.getAxiosInstance(), []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [impact, setImpact] = useState<ImpactResponse | null>(null);
  const [clientNames, setClientNames] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!request) return;
    setLoading(true);
    setError(null);
    setImpact(null);
    try {
      const res = await axios.post<ImpactResponse>(
        '/v2/config-templates/preview-impact',
        request,
      );
      setImpact(res.data);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ??
        (err as { message?: string })?.message ??
        'Unknown error';
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }, [request, axios]);

  useEffect(() => {
    if (isOpen && request) {
      load();
    } else {
      // Reset when the modal closes so a re-open starts fresh.
      setImpact(null);
      setError(null);
    }
  }, [isOpen, request, load]);

  // Resolve client names for the by_client breakdown. clientService caches,
  // so the cost is paid once per session.
  useEffect(() => {
    if (!isOpen || !impact?.by_client?.length) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await clientService.fetchAllClients();
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const c of list) {
          map[c.id] = (c as { clientName?: string; name?: string }).clientName
            || (c as { name?: string }).name
            || c.id;
        }
        setClientNames(map);
      } catch {
        // Silent — fall back to displaying client UUIDs.
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, impact]);

  const summaryLine = useMemo(() => {
    if (!impact) return '';
    if (impact.total_affected === 0) return t('templateImpact.noChanges');
    return t('templateImpact.showingAll', { total: impact.total_affected });
  }, [impact, t]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="template-impact-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="header-title-section">
            <h2>{t('templateImpact.title')}</h2>
            <p className="modal-subtitle">{t('templateImpact.subtitle')}</p>
          </div>
          <button
            className="close-button"
            onClick={onClose}
            disabled={confirming}
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>

        <div className="modal-content">
          {loading && (
            <div className="impact-loading">
              <div className="loading-spinner" />
              <p>{t('templateImpact.loading')}</p>
            </div>
          )}

          {error && !loading && (
            <div className="impact-error">
              <p>{t('templateImpact.errorLoading')}</p>
              <pre className="impact-error-detail">{error}</pre>
              <button className="btn btn-secondary" onClick={load}>
                {t('templateImpact.retry')}
              </button>
            </div>
          )}

          {impact && !loading && !error && (
            <>
              <div className="impact-summary">
                <div className="impact-counter">
                  <span className="impact-counter-value">
                    {impact.total_in_scope}
                  </span>
                  <span className="impact-counter-label">
                    {t('templateImpact.inScope')}
                  </span>
                </div>
                <div
                  className={`impact-counter ${
                    impact.total_affected > 0 ? 'warning' : 'success'
                  }`}
                >
                  <span className="impact-counter-value">
                    {impact.total_affected}
                  </span>
                  <span className="impact-counter-label">
                    {t('templateImpact.affected')}
                  </span>
                </div>
              </div>

              <div className="impact-summary-line">{summaryLine}</div>

              {impact.total_in_scope === 0 && (
                <div className="impact-empty">
                  {t('templateImpact.noDevicesInScope')}
                </div>
              )}

              {impact.by_client && impact.by_client.length > 0 && (
                <ClientImpactBreakdown
                  rows={impact.by_client}
                  clientNames={clientNames}
                />
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={confirming}
          >
            {t('templateImpact.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onConfirm}
            disabled={loading || confirming || !!error}
          >
            {confirming
              ? t('templateImpact.confirming')
              : t('templateImpact.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Per-client breakdown card. Compact table: name | in-scope | affected |
 * percentage-bar. Rows are already sorted by scope size server-side, but we
 * cap the display to the top N and fold the rest into a single "others"
 * row so the modal stays a glance, not a report.
 */
const CLIENT_ROW_LIMIT = 8;

const ClientImpactBreakdown: React.FC<{
  rows: ClientImpact[];
  clientNames: Record<string, string>;
}> = ({ rows, clientNames }) => {
  const { t } = useI18n();

  // Only list clients that actually have at least one affected device.
  // Non-impacted clients (0 or not-sampled) still contribute to the totals
  // rendered above the table — keeping them out of the list lets the
  // reviewer focus on where the change lands.
  const impactedRows = useMemo(
    () => rows.filter((r) => (r.total_affected ?? 0) > 0),
    [rows],
  );

  const { top, rest } = useMemo(() => {
    if (impactedRows.length <= CLIENT_ROW_LIMIT) return { top: impactedRows, rest: null };
    const head = impactedRows.slice(0, CLIENT_ROW_LIMIT);
    const tail = impactedRows.slice(CLIENT_ROW_LIMIT);
    const folded: ClientImpact = {
      client_id: null,
      total_in_scope: tail.reduce((a, r) => a + r.total_in_scope, 0),
      total_affected: tail.reduce((acc: number | null, r) => {
        if (r.total_affected === null) return acc;
        return (acc ?? 0) + r.total_affected;
      }, 0 as number | null),
      sampled: tail.reduce((a, r) => a + r.sampled, 0),
    };
    return { top: head, rest: { ...folded, _count: tail.length } };
  }, [impactedRows]);

  if (impactedRows.length === 0) return null;

  const renderName = (row: ClientImpact, isRest = false, restCount = 0) => {
    if (isRest) {
      return (
        <span className="client-impact-rest-label">
          {t('templateImpact.otherClients', { count: restCount })}
        </span>
      );
    }
    if (!row.client_id) {
      return <span className="subtle">{t('templateImpact.clientUnassigned')}</span>;
    }
    const name = clientNames[row.client_id];
    if (name && name !== row.client_id) {
      return <span className="client-impact-name">{name}</span>;
    }
    return <span className="monospace subtle">{row.client_id.slice(0, 8)}…</span>;
  };

  const renderAffected = (row: ClientImpact) => {
    if (row.total_affected === null) {
      return (
        <span
          className="subtle"
          title={t('templateImpact.notSampledHint')}
        >
          —
        </span>
      );
    }
    const pct = row.total_in_scope === 0
      ? 0
      : Math.round((row.total_affected / row.total_in_scope) * 100);
    return (
      <div className="client-impact-affected-cell">
        <span className="client-impact-affected-num">{row.total_affected}</span>
        <div className="client-impact-bar" aria-hidden="true">
          <div
            className={`client-impact-bar-fill ${pct === 0 ? 'zero' : pct < 100 ? 'partial' : 'full'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="client-impact-pct">{pct}%</span>
      </div>
    );
  };

  return (
    <section className="client-impact-section">
      <h3 className="client-impact-heading">
        {t('templateImpact.byClient')}
      </h3>
      <table className="client-impact-table">
        <thead>
          <tr>
            <th>{t('templateImpact.client')}</th>
            <th className="client-impact-num-col">{t('templateImpact.devicesOfType')}</th>
            <th>{t('templateImpact.devicesAffected')}</th>
          </tr>
        </thead>
        <tbody>
          {top.map((row, i) => (
            <tr key={row.client_id || `unassigned-${i}`}>
              <td>{renderName(row)}</td>
              <td className="client-impact-num-col">{row.total_in_scope}</td>
              <td>{renderAffected(row)}</td>
            </tr>
          ))}
          {rest && (
            <tr className="client-impact-rest-row">
              <td>{renderName(rest, true, (rest as ClientImpact & { _count: number })._count)}</td>
              <td className="client-impact-num-col">{rest.total_in_scope}</td>
              <td>{renderAffected(rest)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
};

export default TemplateImpactPreviewModal;
